import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, CheckCircle2, AlertTriangle, XCircle, Plug, Link2 } from "lucide-react";

const STATUS_META = {
  passing: { color: "#34C759", icon: CheckCircle2, label: "PASS" },
  warning: { color: "#FFCC00", icon: AlertTriangle, label: "WARN" },
  failing: { color: "#FF3B30", icon: XCircle, label: "FAIL" },
  unknown: { color: "#8E9196", icon: AlertTriangle, label: "N/A" },
};

export default function Compliance() {
  const [controls, setControls] = useState([]);
  const [summary, setSummary] = useState({});
  const [running, setRunning] = useState(false);
  const [selectedFw, setSelectedFw] = useState("all");
  const [connectors, setConnectors] = useState([]);
  const [types, setTypes] = useState({});
  const [binding, setBinding] = useState(null);
  const [bindForm, setBindForm] = useState({ mode: "connector", connector_id: "", evaluator: "", manual_status: "passing" });

  const load = async () => {
    const [c, s, cn, ty] = await Promise.all([
      api.get("/compliance/controls"),
      api.get("/compliance/summary"),
      api.get("/admin/connectors").catch(() => ({ data: { connectors: [] } })),
      api.get("/admin/connectors/types").catch(() => ({ data: { types: {} } })),
    ]);
    setControls(c.data);
    setSummary(s.data);
    setConnectors(cn.data.connectors || []);
    setTypes(ty.data.types || {});
  };

  useEffect(() => { load(); }, []);

  const runCheck = async () => {
    setRunning(true);
    try {
      const r = await api.post("/compliance/run-check");
      toast.success(`Scan complete — ${r.data.auto_evaluated} auto-evaluated, ${r.data.baseline_seeded} seeded`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Scan failed");
    } finally {
      setRunning(false);
    }
  };

  const submitBinding = async () => {
    if (!binding) return;
    try {
      const body = bindForm.mode === "connector"
        ? { connector_id: bindForm.connector_id, evaluator: bindForm.evaluator }
        : { manual_status: bindForm.manual_status };
      await api.patch(`/compliance/controls/${binding.control_id}/binding`, body);
      toast.success("Binding updated");
      setBinding(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bind failed");
    }
  };

  const unbind = async (ctrl) => {
    if (!window.confirm(`Unbind ${ctrl.control_id}?`)) return;
    await api.patch(`/compliance/controls/${ctrl.control_id}/binding`, { connector_id: null });
    toast.success("Unbound");
    load();
  };

  const frameworks = Object.keys(summary);
  const filtered = selectedFw === "all" ? controls : controls.filter((c) => c.framework === selectedFw);

  // Which connectors can bind to which controls? Filter to ones with evaluators.
  const eligibleConnectors = connectors.filter((c) => c.enabled && (types[c.type]?.evaluators?.length || 0) > 0);

  return (
    <div className="p-8 max-w-[1400px] mx-auto">
      <header className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-widest text-[#8E9196]">Continuous compliance monitoring · tenant-scoped</div>
          <h1 className="font-display font-black text-5xl mt-2">Compliance</h1>
          <p className="text-[#8E9196] mt-1 text-sm">
            Bind any control to a <a href="/admin/connectors" className="text-[#00F0FF] hover:underline">Connector</a> to auto-evaluate with real evidence.
          </p>
        </div>
        <button onClick={runCheck} disabled={running} data-testid="run-compliance-scan"
          className="inline-flex items-center gap-2 border border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF] hover:text-black transition-colors px-3 py-2 rounded-sm font-mono uppercase text-xs tracking-widest disabled:opacity-50">
          <RefreshCw className={`w-3.5 h-3.5 ${running ? "animate-spin" : ""}`} /> {running ? "Scanning…" : "Run Compliance Scan"}
        </button>
      </header>

      {/* Framework summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-8" data-testid="compliance-summary-tiles">
        {frameworks.map((fw) => {
          const stats = summary[fw];
          return (
            <button key={fw} onClick={() => setSelectedFw(fw)}
              data-testid={`fw-tile-${fw.replace(/\s/g, "-")}`}
              className={`text-left border rounded-sm p-4 transition-colors ${
                selectedFw === fw ? "border-[#00F0FF]" : "border-white/10 hover:border-white/25"
              } bg-[#121212]`}>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196]">{fw}</div>
              <div className="font-display font-black text-3xl mt-1">{stats.score ?? 0}<span className="text-lg text-[#8E9196]">%</span></div>
              <div className="mt-2 flex gap-2 text-[10px] font-mono flex-wrap">
                <span style={{ color: "#34C759" }}>{stats.passing || 0} pass</span>
                <span style={{ color: "#FFCC00" }}>{stats.warning || 0} warn</span>
                <span style={{ color: "#FF3B30" }}>{stats.failing || 0} fail</span>
              </div>
            </button>
          );
        })}
        <button onClick={() => setSelectedFw("all")} data-testid="fw-tile-all"
          className={`text-left border rounded-sm p-4 transition-colors ${
            selectedFw === "all" ? "border-[#00F0FF]" : "border-white/10 hover:border-white/25"
          } bg-[#121212]`}>
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196]">All</div>
          <div className="font-display font-black text-3xl mt-1">{controls.length}</div>
          <div className="mt-2 text-[10px] font-mono text-[#8E9196]">controls total</div>
        </button>
      </div>

      {/* Controls table */}
      <div className="border border-white/10 bg-[#121212] rounded-sm overflow-hidden" data-testid="controls-table">
        <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-widest text-[#8E9196]">Controls</div>
            <div className="font-display text-xl">{selectedFw === "all" ? "All frameworks" : selectedFw} · {filtered.length}</div>
          </div>
          <div className="flex items-center gap-3 text-[10px] font-mono uppercase tracking-widest text-[#8E9196]">
            {Object.entries(STATUS_META).slice(0, 3).map(([k, m]) => (
              <span key={k} className="inline-flex items-center gap-1.5">
                <span className="w-2 h-2" style={{ background: m.color }} /> {k}
              </span>
            ))}
          </div>
        </div>

        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10 text-[10px] font-mono uppercase tracking-widest text-[#8E9196]">
              <th className="text-left px-5 py-3">Framework</th>
              <th className="text-left px-5 py-3">Control</th>
              <th className="text-left px-5 py-3">Title / Evidence</th>
              <th className="text-left px-5 py-3">Status</th>
              <th className="text-left px-5 py-3">Source</th>
              <th className="text-right px-5 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => {
              const meta = STATUS_META[c.status] || STATUS_META.unknown;
              return (
                <tr key={c.control_id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`ctrl-${c.control_id}`}>
                  <td className="px-5 py-2.5 font-mono text-xs text-[#8E9196]">{c.framework}</td>
                  <td className="px-5 py-2.5 font-mono text-xs">{c.control_id}</td>
                  <td className="px-5 py-2.5">
                    <div>{c.title}</div>
                    {c.evidence && <div className="text-[11px] text-[#8E9196] mt-0.5 font-mono truncate max-w-md">{c.evidence}</div>}
                  </td>
                  <td className="px-5 py-2.5">
                    <span className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest" style={{ color: meta.color }}>
                      <meta.icon className="w-3 h-3" /> {meta.label}
                    </span>
                  </td>
                  <td className="px-5 py-2.5">
                    {c.auto ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-[#B57BFF] border border-[#B57BFF]/40 px-1.5 py-0.5">
                        <Plug className="w-3 h-3" /> auto · {c.connector_type}
                      </span>
                    ) : (
                      <span className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196]">manual</span>
                    )}
                  </td>
                  <td className="px-5 py-2.5 text-right">
                    {c.auto ? (
                      <button onClick={() => unbind(c)} data-testid={`unbind-${c.control_id}`}
                        className="text-[10px] font-mono uppercase tracking-widest text-[#FF3B30] hover:text-white border border-[#FF3B30]/40 hover:border-[#FF3B30] px-2 py-1">
                        Unbind
                      </button>
                    ) : (
                      <button onClick={() => { setBinding(c); setBindForm({ mode: "connector", connector_id: eligibleConnectors[0]?.id || "", evaluator: "", manual_status: c.status || "passing" }); }} data-testid={`bind-${c.control_id}`}
                        className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-[#00F0FF] hover:text-black hover:bg-[#00F0FF] border border-[#00F0FF]/40 hover:border-[#00F0FF] px-2 py-1">
                        <Link2 className="w-3 h-3" /> Bind
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Bind dialog */}
      {binding && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={(e) => e.target === e.currentTarget && setBinding(null)} data-testid="bind-dialog">
          <div className="bg-[#0a0a0a] border border-white/15 rounded-sm max-w-lg w-full p-6">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#00F0FF] mb-1">Bind control</div>
            <div className="font-display font-black text-2xl mb-1">{binding.control_id}</div>
            <div className="text-[#8E9196] text-sm mb-4">{binding.title}</div>

            <div className="flex gap-2 mb-4">
              <button onClick={() => setBindForm({ ...bindForm, mode: "connector" })}
                className={`flex-1 px-3 py-2 border text-xs font-mono uppercase tracking-widest ${
                  bindForm.mode === "connector" ? "border-[#00F0FF] text-[#00F0FF]" : "border-white/15 text-[#8E9196]"}`}>
                Auto-evaluate
              </button>
              <button onClick={() => setBindForm({ ...bindForm, mode: "manual" })}
                className={`flex-1 px-3 py-2 border text-xs font-mono uppercase tracking-widest ${
                  bindForm.mode === "manual" ? "border-[#00F0FF] text-[#00F0FF]" : "border-white/15 text-[#8E9196]"}`}>
                Manual attest
              </button>
            </div>

            {bindForm.mode === "connector" && (
              <>
                <label className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196]">Connector</label>
                <select value={bindForm.connector_id}
                  onChange={(e) => {
                    const cn = connectors.find((c) => c.id === e.target.value);
                    const evals = cn ? (types[cn.type]?.evaluators || []) : [];
                    setBindForm({ ...bindForm, connector_id: e.target.value, evaluator: evals[0] || "" });
                  }}
                  data-testid="bind-connector"
                  className="w-full mt-1 mb-3 bg-transparent border border-white/15 px-3 py-2 rounded-sm text-sm">
                  <option value="" className="bg-[#0a0a0a]">— select —</option>
                  {eligibleConnectors.map((c) => (
                    <option key={c.id} value={c.id} className="bg-[#0a0a0a]">{c.name} ({types[c.type]?.label})</option>
                  ))}
                </select>
                {bindForm.connector_id && (
                  <>
                    <label className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196]">Evaluator</label>
                    <select value={bindForm.evaluator}
                      onChange={(e) => setBindForm({ ...bindForm, evaluator: e.target.value })}
                      data-testid="bind-evaluator"
                      className="w-full mt-1 mb-3 bg-transparent border border-white/15 px-3 py-2 rounded-sm text-sm">
                      {(types[connectors.find((c) => c.id === bindForm.connector_id)?.type]?.evaluators || []).map((e) => (
                        <option key={e} value={e} className="bg-[#0a0a0a]">{e}</option>
                      ))}
                    </select>
                  </>
                )}
                {eligibleConnectors.length === 0 && (
                  <div className="text-xs text-[#FFCC00]">
                    No connectors with evaluators enabled. <a href="/admin/connectors" className="text-[#00F0FF] hover:underline">Add one</a> first.
                  </div>
                )}
              </>
            )}

            {bindForm.mode === "manual" && (
              <>
                <label className="text-[10px] font-mono uppercase tracking-widest text-[#8E9196]">Attested status</label>
                <select value={bindForm.manual_status}
                  onChange={(e) => setBindForm({ ...bindForm, manual_status: e.target.value })}
                  data-testid="bind-manual-status"
                  className="w-full mt-1 mb-3 bg-transparent border border-white/15 px-3 py-2 rounded-sm text-sm">
                  <option value="passing" className="bg-[#0a0a0a]">Passing</option>
                  <option value="warning" className="bg-[#0a0a0a]">Warning</option>
                  <option value="failing" className="bg-[#0a0a0a]">Failing</option>
                </select>
              </>
            )}

            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setBinding(null)}
                className="px-3 py-2 border border-white/15 text-white/80 rounded-sm font-mono uppercase text-xs tracking-widest">Cancel</button>
              <button onClick={submitBinding} data-testid="submit-bind"
                className="px-3 py-2 border border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF] hover:text-black rounded-sm font-mono uppercase text-xs tracking-widest">
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
